import axios from 'axios';
import {CLAIMS_TYPES, CLAIMS_TYPES_INFO} from '@/views/RenovaDashboard/ClaimsParts/constants';
import {cloneDeep} from 'lodash';

const defaultFilters = {
  policyType: undefined,
  searchQuery: undefined,
  claimDateFrom: null,
  claimDateTo: null,
  eventDateFrom: null,
  eventDateTo: null,
  insuranceCompanyId: undefined,
  agentId: undefined,
  currencyCodes: undefined,
};
const state = {
  claims: {
    items: [],
    totalCount: 0,
    loading: false,
  },
  claimDetails: {
    data: null,
    loading: false,
    actions: {
      items: [],
      totalCount: 0,
      loading: false,
    },
  },
  filters: { ...defaultFilters },
  claimsGroups: [],
};

const getters = {
  claimList: () => state.claims.items,
  claimTotalCount: () => state.claims.totalCount,
  claimsLoading: () => state.claims.loading,
  claimsFilters: () => state.filters,
  claimDetails: () => state.claimDetails.data,
  claimDetailsLoading: () => state.claimDetails.loading,
  claimDetailsActions: () => state.claimDetails.actions,
  claimsGroups: () => state.claimsGroups,
  customClaimsGroups: () => state.claimsGroups?.filter((group) => !!group.customStageId),
};

// Mock data for claim details
const mockClaimDetails = {
  claimId: '1',
  title: 'Medical Claim 1',
  email: 'patient1@example.com',
  phone: '+1234567890',
  stage: 'NEW',
  createAt: new Date().toISOString(),
  dealPremium: 1500,
  policyType: 'INDIVIDUAL',
  firstName: 'John',
  lastName: 'Doe',
  birthdate: '1980-01-01',
  sex: 'MALE',
  paymentFormat: 'MONTHLY',
  agentCommissionPaymentStatus: 'PENDING',
  clientAcquisitionChannel: 'WEBSITE',
  insuranceCompany: { id: '1', name: 'Insurance Co A' },
  agent: { id: '1', name: 'Agent Smith' },
  agentCommission: 150,
  paymentStatus: 'PENDING',
  transactionStatus: 'PENDING',
  policy: {
    program: {
      programId: 'a0dbdf80-e476-48b0-b2bd-2c562357ad90',
      name: 'plan-20240514001',
      description: '',
      insuranceType: 'HEALTH_INSURANCE'
    },
    insurancePolicyId: '6cfa4200-359d-4056-82bd-a2922917bc94',
    createdAt: '2024-08-13T17:13:14.353Z',
    number: 'new-number-002',
    personName: '1111112 9919234-01',
    validity: {
      from: '2024-09-01T00:00:00.000Z',
      to: '2024-09-30T00:00:00.000Z'
    },
    birthdate: '2024-07-18T00:00:00.000Z',
    certificate: 'new-certificate-number-02',
    activeStatus: 'NOT_ACTIVE',
    documents: [],
    planDocuments: [],
    policyFile: {},
    certificateFile: {},
    planDescriptionFile: {
      id: 'aff1f42d-bb1e-47f6-9514-a46ace0acbe8',
      name: 'Policy 23-24 (1).pdf',
      fileName: 'Policy 23-24 (1).pdf',
      url: 'https://renova-file-eu-central-1-dev.s3.eu-central-1.amazonaws.com/aff1f42d-bb1e-47f6-9514-a46ace0acbe8.pdf'
    }
  },
  documents: [
    {
      id: '1',
      name: 'Medical Report',
      type: 'pdf',
      url: '#',
      createdAt: new Date().toISOString()
    }
  ]
};

// Mock data for claim actions
const mockClaimActions = {
  items: [
    {
      action: 'CLAIM_CREATED',
      details: {
        agent: 'Agent Smith'
      },
      createdAt: new Date().toISOString()
    },
    {
      action: 'DOCUMENT_ADDED',
      details: {
        documentName: 'Medical Report'
      },
      createdAt: new Date(Date.now() - 86400000).toISOString() // 1 day ago
    },
    {
      action: 'CLAIM_UPDATED_STAGE',
      details: {
        stageNew: 'IN_REVIEW',
        stagePrev: 'NEW'
      },
      createdAt: new Date(Date.now() - 172800000).toISOString() // 2 days ago
    },
    {
      action: 'CLAIM_UPDATED',
      details: {
        field: 'claimAmount',
        valueNew: '1500',
        valuePrev: '1000'
      },
      createdAt: new Date(Date.now() - 259200000).toISOString() // 3 days ago
    }
  ],
  totalCount: 4
};

export function getClaims(filters = {}) {
  return axios.post('/insurance/claim/list', filters, {
    headers: {
      'X-Locale': 'en',
    },
  });
}

const actions = {
  fetchClaims({ commit, state }) {
    commit('setClaimsListLoading', true);
    getClaims(state.filters)
      .then((response) => {
        commit('setClaims', response.data);
        commit('setClaimsListLoading', false);
      })
      .catch((error) => {
        console.error('Error fetching claims:', error);
        commit('setClaimsListLoading', false);
      });
  },

  updateClaims({ commit, dispatch }, claims) {
    commit('setUpdatedClaims', claims);

    const updatePromises = claims.map((claim) => axios.post('/insurance/claim/save', {
      claimId: claim.claimId,
      sortOrder: claim.sortOrderSave,
      stage: claim.stageSave,
    }, {
      headers: {
        'X-Locale': 'en',
      },
    })
      .catch((error) => {
        console.error(`Error updating claim ${claim.claimId}:`, error);
        return Promise.reject(error);
      }));

    // Wait for all updates to complete
    return Promise.all(updatePromises)
      .then(() => {
        // Refresh the claims list to ensure we have the latest data
        dispatch('fetchClaims');
      })
      .catch((error) => {
        console.error('Error updating claims:', error);
        // Refresh the claims list to ensure we have the latest data even if there was an error
        dispatch('fetchClaims');
        return Promise.reject(error);
      });
  },

  deleteClaims({ commit, dispatch }, claimIds) {
    const deletePromises = claimIds.map((claimId) => axios.post('/insurance/claim/delete', {
      claimId,
    }, {
      headers: {
        'X-Locale': 'en',
      },
    })
      .catch((error) => {
        console.error(`Error deleting claim ${claimId}:`, error);
        return Promise.reject(error);
      }));

    return Promise.all(deletePromises)
      .then(() => {
        commit('removeClaims', claimIds);
        dispatch('fetchClaims');
      })
      .catch((error) => {
        console.error('Error deleting claims:', error);
        return Promise.reject(error);
      });
  },

  fetchClaimDetails({ commit, dispatch }, claimId) {
    commit('setDetailsLoading', true);
    dispatch('fetchClaimActions', claimId);

    return axios.post('/insurance/claim/detail', { claimId }, {
      headers: {
        'X-Locale': 'en',
      },
    })
      .then((response) => {
        commit('setClaimDetails', response.data);
        commit('setDetailsLoading', false);
      })
      .catch((error) => {
        console.error('Error fetching claim details:', error);
        commit('setDetailsLoading', false);
      });
  },
  fetchClaimActions({ commit }, claimId) {
    commit('setActionsLoading', true);
    // Use mock data instead of making an API call
    setTimeout(() => {
      commit('setClaimActions', mockClaimActions);
      commit('setActionsLoading', false);
    }, 500); // Add a small delay to simulate network request
  },
  saveClaim({ commit, dispatch }, body) {
    return axios.post('/insurance/claim/save', body, {
      headers: {
        'X-Locale': 'en',
      },
    })
      .then((response) => {
        if (body.claimId) {
          // If it's an existing claim, update it
          dispatch('fetchClaimDetails', body.claimId);
        }
        dispatch('fetchClaims');
        return response;
      })
      .catch((error) => {
        console.error('Error saving claim:', error);
        return Promise.reject(error);
      });
  },
  addClaimFile({ commit, dispatch }, body) {
    setTimeout(() => {
      if (body.documentCdnFileIdsSave && body.documentCdnFileIdsSave.length > 0) {
        const newDoc = {
          id: body.documentCdnFileIdsSave[0],
          name: 'New Document',
          type: 'pdf',
          url: '#',
          createdAt: new Date().toISOString()
        };
        mockClaimDetails.documents = [...(mockClaimDetails.documents || []), newDoc];
      }
      dispatch('fetchClaimDetails', body.claimId);
    }, 500); // Add a small delay to simulate network request
  },

  deleteClaimFile({ commit, dispatch }, body) {
    // Use mock data instead of making an API call
    setTimeout(() => {
      // Update mockClaimDetails to remove the file
      if (body.documentCdnFileIdsDelete && body.documentCdnFileIdsDelete.length > 0) {
        mockClaimDetails.documents = mockClaimDetails.documents.filter(
          (doc) => !body.documentCdnFileIdsDelete.includes(doc.id)
        );
      }
      dispatch('fetchClaimDetails', body.claimId);
    }, 500); // Add a small delay to simulate network request
  },
  updateClaimsFilters({ commit }, filters) {
    commit('setClaimsFilters', filters);
  },
};

const mutations = {
  setClaims(state, claims) {
    state.claims.items = claims.items;
    state.claims.totalCount = claims.totalCount;
  },

  setClaimsListLoading(state, loading) {
    state.claims.loading = loading;
  },

  setClaimDetails(state, details) {
    state.claimDetails.data = details;
  },
  setClaimActions(state, actions) {
    state.claimDetails.actions = actions;
  },

  setDetailsLoading(state, loading) {
    state.claimDetails.loading = loading;
  },
  setActionsLoading(state, loading) {
    state.claimDetails.actions.loading = loading;
  },

  setUpdatedClaims(state, updatedClaims) {
    state.claims.items = state.claims.items.map((claim) => {
      const updatedClaim = updatedClaims.find((val) => val.claimId === claim.claimId);
      if (updatedClaim) {
        return {
          ...claim,
          ...updatedClaim,
          stage: updatedClaim.stageSave,
          sortNumber: updatedClaim.sortNumber,
        };
      }
      return claim;
    });
  },

  removeClaims(state, claimIds) {
    state.claims.items = state.claims.items.filter((claim) => !claimIds.includes(claim.claimId));
  },

  setClaimsFilters(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },

  clearAllFilters(state) {
    state.filters = { ...defaultFilters };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
